// Video Director step ⑤ — what Render will make: the cut as a filmstrip, its numbers,
// warnings, and a full-cut preview (same player as the Edit step). Presentational.

import React, { useState } from "react";
import i18n from "i18next";
import { LuPause, LuPlay, LuVolume2 } from "react-icons/lu";
import VideoThumb from "../../../timeline/IdeaTimeline/components/VideoThumb";
import { PreviewButton } from "./ImagePreview";
import { CutVideos, useCutPlayer } from "./useCutPlayer";
import { FPS, isStale, totalFrames } from "./edl";

const t = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);

const mmss = (frames) => {
  const sec = Math.round(frames / FPS);
  return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
};

export default function DirectorFinish({ project, audioUrl, showPreview }) {
  const { edl, shots } = project;
  const aspect = project.brief?.aspect || "16:9";
  const vertical = aspect === "9:16";
  const total = totalFrames(edl);
  const [sound, setSound] = useState(false);
  const takes = shots.flatMap((s) => s.takes);
  const takeById = new Map(takes.map((tk) => [tk.id, tk]));
  const shotNo = new Map(shots.map((s, i) => [s.id, i + 1]));
  const takeOf = (seg) => takeById.get(seg.takeId);
  const player = useCutPlayer({ edl, takeOf, audioUrl, sound, clipAudio: project.clipAudio });
  const inCut = new Set(edl.map((seg) => seg.takeId));
  const outside = takes.filter((tk) => !inCut.has(tk.id));
  const pending = outside.filter((tk) => tk.status === "pending" && !isStale(tk)).length;
  const failed = outside.filter((tk) => tk.status === "failed" || isStale(tk)).length;
  const warnings = [
    edl.length === 0 && t("FinishWarnEmpty"),
    pending > 0 && t("FinishWarnPending", { count: pending }),
    failed > 0 && t("FinishWarnFailed", { count: failed }),
  ].filter(Boolean);
  const playable = edl.filter((seg) => takeOf(seg)?.url);
  const clips = playable.map((seg) => ({
    url: takeOf(seg).url,
    kind: "video",
    seekSec: (seg.inFrame + 0.5) / FPS,
    name: t("Shot", { n: shotNo.get(seg.shotId) }),
  }));
  const play = (withSound) => {
    setSound(withSound);
    player.togglePlay();
  };

  return (
    <div className="add-video-modal__director-finish">
      {warnings.length > 0 && (
        <ul role="status" className="add-video-modal__director-finish-warn">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
      <div className="add-video-modal__director-filmstrip" role="list">
        {edl.map((seg) => {
          const tk = takeOf(seg);
          const len = seg.outFrame - seg.inFrame;
          return (
            <figure key={seg.id} role="listitem" className="add-video-modal__director-finish-clip">
              {tk?.url && (
                <VideoThumb
                  url={tk.url}
                  seekSec={(seg.inFrame + 0.5) / FPS}
                  className="add-video-modal__director-film-thumb"
                />
              )}
              {tk?.url && <PreviewButton items={clips} index={playable.indexOf(seg)} />}
              <figcaption>
                {t("Shot", { n: shotNo.get(seg.shotId) })} ·{" "}
                {tk?.source === "import" ? t("Imported") : t("Generated")} · {mmss(len)}
              </figcaption>
            </figure>
          );
        })}
      </div>
      <div className="add-video-modal__director-finish-stats">
        <span>{mmss(total)}</span>
        <span>{t("Clips", { count: edl.length })}</span>
        <span>{aspect} · 720p</span>
      </div>
      <p className="add-video-modal__director-finish-explain">
        {t("FinishExplain", { count: edl.length, duration: mmss(total), aspect })}
      </p>
      {showPreview && edl.length > 0 && (
        <div className="add-video-modal__director-finish-preview" aria-label={t("CutPreview")}>
          <div
            className="add-video-modal__director-preview"
            style={{ aspectRatio: vertical ? "9/16" : "16/9" }}
          >
            <CutVideos player={player} sound={sound} audioUrl={audioUrl} />
          </div>
          <div className="add-video-modal__director-finish-play">
            {player.playing ? (
              <button
                type="button"
                className="app-modal__btn app-modal__btn--ghost"
                onClick={player.togglePlay}
              >
                <LuPause size={16} />
                {t("Pause")}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="app-modal__btn app-modal__btn--ghost"
                  onClick={() => play(false)}
                >
                  <LuPlay size={16} />
                  {t("Play")}
                </button>
                <button
                  type="button"
                  className="app-modal__btn app-modal__btn--ghost"
                  disabled={!audioUrl}
                  title={audioUrl ? undefined : t("NoNarration")}
                  onClick={() => play(true)}
                >
                  <LuVolume2 size={16} />
                  {t("PlaySound")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
